/**
 * Guide modal content for each section of the club dashboard.
 * Kept in a separate file so copy can be updated without touching components.
 */

const guideContent = {
  overview: {
    title: 'Dashboard Overview',
    sections: [
      {
        body: 'A snapshot of your outreach activity and the prospects near your club.',
      },
      {
        heading: 'Stat Cards',
        items: [
          { term: 'Total Requests Sent', definition: 'The total number of interaction requests your club has sent to golfers, regardless of status.' },
          { term: 'Pending Approval', definition: 'Requests where the golfer hasn\'t responded yet.' },
          { term: 'Approved Connections', definition: 'Golfers who accepted your request.' },
          { term: 'In Conversation', definition: 'Approved golfers who have sent you at least one message back.' },
        ],
      },
      {
        heading: 'Prospect Insights',
        body: 'Aggregate stats for privateIQ users with active subscriptions within your search radius — total count, average age, average distance from your club, and average household income. The map shows approximate locations (jittered for privacy).',
      },
    ],
  },

  myclub: {
    title: 'My Club',
    sections: [
      {
        body: 'Your club\'s profile and membership information.',
      },
      {
        heading: 'Key Terms',
        items: [
          { term: 'Ownership', definition: 'Whether your club is member-owned or managed by a parent company (Troon, Invited, Concert Golf Partners, etc.).' },
          { term: 'True Equity Club', definition: 'Members would receive proportional proceeds if the club were sold.' },
          { term: 'Young Executive', definition: 'An age-limited membership category. Set the max age cutoff.' },
          { term: 'National / Non-Resident', definition: 'A membership tier for golfers who live beyond a certain distance.' },
          { term: 'Capital Charge', definition: 'An annual facility assessment beyond regular dues.' },
          { term: 'F&B Obligation', definition: 'A required monthly food & beverage minimum spend.' },
        ],
      },
      {
        body: 'Completing all fields makes your club eligible to submit for IQ Edge.',
      },
    ],
  },

  nearby: {
    title: 'Discover Prospects',
    sections: [
      {
        body: 'privateIQ users with active subscriptions who are within your search radius and haven\'t been contacted by your club yet. Use the radius slider to adjust your search area (1–100 miles).',
      },
      {
        heading: 'Key Terms',
        items: [
          { term: 'Request Info', definition: 'Sends the golfer a notification that your club is interested in connecting. They can approve or deny in the app. On the free plan, each request counts toward your limit.' },
          { term: 'Current Member', definition: 'Whether this golfer indicated they are already a member at another private club.' },
          { term: 'Distance', definition: 'Approximate distance from your club to the golfer. This is based on the zip code the golfer provided when signing up, not their exact address.' },
        ],
      },
      {
        body: 'Users who signed up in the last 7 days do not appear here — they are reserved for clubs with IQ Edge.',
      },
    ],
  },

  iq_edge: {
    title: 'IQ Edge',
    sections: [
      {
        body: 'Golfers who signed up to privateIQ in the last 7 days within your search radius. IQ Edge gives your club early access to new users before they appear in the Discover Prospects tab for all clubs. There is no additional cost.',
      },
      {
        heading: 'Key Terms',
        items: [
          { term: 'How to qualify', definition: 'Complete all membership info fields on the My Club tab and submit for review. Approval is manual.' },
          { term: 'Duration', definition: 'IQ Edge access lasts 365 days from approval.' },
        ],
      },
    ],
  },

  pending: {
    title: 'Pending Requests',
    sections: [
      {
        body: 'Interaction requests you\'ve sent that the golfer hasn\'t responded to yet.',
      },
      {
        body: 'Golfers with pending requests receive automated reminders — email reminders starting at 24 hours and continuing every 2 days, push notifications every 3 days at 8:30 PM, and SMS reminders on Sunday and Tuesday evenings.',
      },
      {
        heading: 'Send Reminder',
        body: 'Sends the golfer an SMS text message asking them to review your request. You can send up to 3 manual reminders, with a 72-hour cooldown between each. This is separate from the automated reminders.',
      },
    ],
  },

  approved: {
    title: 'Approved Connections',
    sections: [
      {
        body: 'Golfers who accepted your request, organized by pipeline stage.',
      },
      {
        heading: 'Pipeline Stages',
        items: [
          { term: 'Warm Lead', definition: 'The golfer approved your request. This is the default starting stage.' },
          { term: 'Added to Waitlist', definition: 'You\'ve placed this golfer on your club\'s membership waitlist.' },
          { term: 'Member — Joined Club', definition: 'The golfer has become a member at your club.' },
          { term: 'Retire Lead — Will Not Join', definition: 'Marks this golfer as not joining. After a 2-hour delay, the golfer will receive a feedback survey asking about their interaction experience and why it did not work out. If you change the stage back before the survey is sent, it will be cancelled.' },
        ],
      },
    ],
  },

  conversations: {
    title: 'Conversations',
    sections: [
      {
        body: 'Your in-app message threads with golfers who have approved your connection request.',
      },
      {
        body: 'Messages sent here are delivered within the privateIQ app and website — they are not SMS text messages or emails. When you send a message, the golfer receives a push notification on their phone letting them know.',
      },
      {
        heading: 'Filter Tabs',
        items: [
          { term: 'All', definition: 'Every approved connection, whether or not any messages have been exchanged.' },
          { term: 'Active', definition: 'Connections where at least one message has been sent (by you or the golfer).' },
          { term: 'In Conversation', definition: 'Connections where the golfer has replied to you at least once.' },
          { term: 'Awaiting Reply', definition: 'Connections where you\'ve sent a message but the golfer hasn\'t responded yet.' },
          { term: 'New', definition: 'Approved connections where no messages have been exchanged yet. The badge shows the count.' },
        ],
      },
      {
        heading: 'Sending Messages',
        body: 'You can write a message, attach a PDF file, or both. PDF is the only supported file type, with a 25MB size limit. This is a good way to share your membership packet or course information.',
      },
      {
        heading: 'Prospect Profile Panel',
        body: 'On desktop, click the golfer\'s avatar in the chat header to open a side panel showing their contact info (email and phone, with copy buttons), demographics (city, age, children, income, marital status, current club membership), and their current pipeline stage. You can change the pipeline stage directly from this panel.',
      },
    ],
  },

  feedback: {
    title: 'Feedback',
    sections: [
      {
        body: 'Responses from golfers explaining why they didn\'t connect or join.',
      },
      {
        heading: 'Where Feedback Comes From',
        items: [
          { term: 'Declined requests', definition: 'When a golfer denies your interaction request, their reason is captured automatically (Not Interested, Too Far, Already a Member, Bad Timing, or Other).' },
          { term: 'Retired leads', definition: 'When you retire a lead from the pipeline, a conversational feedback flow is triggered after a 2-hour delay. The golfer is asked for their reasons, a 1–5 rating, and open-ended feedback. The golfer must actively respond for this data to be collected.' },
        ],
      },
      {
        heading: 'AI Summary',
        body: 'Analyzes patterns across all your feedback responses.',
      },
    ],
  },
};

export default guideContent;
